import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  TrendingUp,
  TrendingDown,
  Search,
  BarChart3,
  Coins,
  Diamond,
  RefreshCw,
  LineChart
} from 'lucide-react';
import { toast } from 'sonner';

// Enhanced TradingView Advanced Chart Widget with error handling
function TradingViewWidget({ symbol = "AAPL", interval = "D" }) {
  const containerRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  
  useEffect(() => {
    setHasError(false);
    
    // Global error handler for TradingView script errors
    const handleScriptError = (event) => {
      if (event.message && event.message.includes('Script error')) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
    };
    
    window.addEventListener('error', handleScriptError);
    
    try {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        
        const script = document.createElement('script');
        script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
        script.type = 'text/javascript';
        script.async = true;
        script.onerror = () => {
          setHasError(true);
        };
        script.innerHTML = JSON.stringify({
          "autosize": true,
          "symbol": symbol.includes(':') ? symbol : `NASDAQ:${symbol}`,
          "interval": interval,
          "timezone": "Etc/UTC",
          "theme": "dark",
          "style": "1",
          "locale": "en",
          "enable_publishing": false,
          "allow_symbol_change": true,
          "calendar": false,
          "hide_top_toolbar": false,
          "hide_legend": false,
          "save_image": true,
          "studies": [
            "RSI@tv-basicstudies",
            "MASimple@tv-basicstudies"
          ],
          "show_popup_button": true,
          "popup_width": "1000",
          "popup_height": "650",
          "support_host": "https://www.tradingview.com"
        });
        
        containerRef.current.appendChild(script);
      }
    } catch (error) {
      setHasError(true);
    }
    
    return () => {
      window.removeEventListener('error', handleScriptError);
    };
  }, [symbol, interval]);

  if (hasError) {
    return (
      <div className="flex items-center justify-center h-[500px] bg-white/5 rounded-lg">
        <div className="text-center">
          <LineChart className="w-12 h-12 text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400">Chart temporarily unavailable</p>
          <p className="text-gray-500 text-sm">Try refreshing the page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tradingview-widget-container" style={{ height: "500px", width: "100%" }}>
      <div ref={containerRef} style={{ height: "100%", width: "100%" }} />
    </div>
  );
}

// TradingView Mini Chart Widget with error handling
function TradingViewMiniChart({ symbol = "AAPL" }) {
  const containerRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  
  useEffect(() => {
    setHasError(false);
    
    try {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        
        const script = document.createElement('script');
        script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js';
        script.type = 'text/javascript';
        script.async = true;
        script.onerror = () => setHasError(true);
        script.innerHTML = JSON.stringify({
          "symbol": symbol.includes(':') ? symbol : `NASDAQ:${symbol}`,
          "width": "100%",
          "height": "100%",
          "locale": "en",
          "dateRange": "1M",
          "colorTheme": "dark",
          "isTransparent": true,
          "autosize": true,
          "largeChartUrl": ""
        });
        
        containerRef.current.appendChild(script);
      }
    } catch (error) {
      setHasError(true);
    }
  }, [symbol]);

  if (hasError) {
    return (
      <div className="flex items-center justify-center h-[220px] bg-white/5 rounded-lg">
        <p className="text-gray-500 text-sm">Chart unavailable</p>
      </div>
    );
  }

  return (
    <div style={{ height: "200px", width: "100%" }}>
      <div ref={containerRef} style={{ height: "100%", width: "100%" }} />
    </div>
  );
}

export default function MarketsPage() {
  const { user, updateUser } = useAuth();
  const { formatCurrency, preferredCurrency, getSymbol } = useCurrency();
  const [stocks, setStocks] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [metals, setMetals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [tradeType, setTradeType] = useState('buy');
  const [quantity, setQuantity] = useState('');
  const [tradeLoading, setTradeLoading] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [chartSymbol, setChartSymbol] = useState('AAPL');
  const [chartInterval, setChartInterval] = useState('D');

  const timeframes = [
    { value: '1', label: '1m' },
    { value: '5', label: '5m' },
    { value: '15', label: '15m' },
    { value: '60', label: '1H' },
    { value: '240', label: '4H' },
    { value: 'D', label: '1D' },
    { value: 'W', label: '1W' },
    { value: 'M', label: '1M' }
  ];

  const fetchMarkets = async () => {
    try {
      const [stocksRes, cryptoRes, metalsRes] = await Promise.all([
        api.get('/market/stocks'),
        api.get('/market/crypto'),
        api.get('/market/metals')
      ]);
      setStocks(stocksRes.data);
      setCrypto(cryptoRes.data);
      setMetals(metalsRes.data);
    } catch (error) {
      toast.error('Failed to fetch market data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarkets();
    const interval = setInterval(fetchMarkets, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value?.toFixed(2) || 0}%`;
  };

  const filterAssets = (assets) => {
    if (!searchQuery) return assets;
    const query = searchQuery.toLowerCase();
    return assets.filter(
      a => a.symbol.toLowerCase().includes(query) || 
           a.name.toLowerCase().includes(query)
    );
  };

  const handleTrade = async () => {
    if (!quantity || parseFloat(quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    const totalCost = parseFloat(quantity) * selectedAsset.price;
    
    if (tradeType === 'buy' && totalCost > (user?.balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setTradeLoading(true);
    try {
      const endpoint = tradeType === 'buy' ? '/portfolio/buy' : '/portfolio/sell';
      const res = await api.post(`${endpoint}?symbol=${selectedAsset.symbol}&quantity=${quantity}`);
      
      toast.success(`Successfully ${tradeType === 'buy' ? 'bought' : 'sold'} ${quantity} ${selectedAsset.symbol}`);
      
      const userRes = await api.get('/auth/me');
      updateUser(userRes.data);
      
      setSelectedAsset(null);
      setQuantity('');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${tradeType}`);
    } finally {
      setTradeLoading(false);
    }
  };

  const openChart = (symbol) => {
    setChartSymbol(symbol);
    setShowChart(true);
  };

  const getTradingViewSymbol = (symbol, type) => {
    if (type === 'crypto') {
      const cryptoMap = {
        'BTC': 'BINANCE:BTCUSDT',
        'ETH': 'BINANCE:ETHUSDT',
        'SOL': 'BINANCE:SOLUSDT',
        'XRP': 'BINANCE:XRPUSDT',
        'ADA': 'BINANCE:ADAUSDT',
        'DOGE': 'BINANCE:DOGEUSDT'
      };
      return cryptoMap[symbol] || `BINANCE:${symbol}USDT`;
    }
    if (type === 'metal') {
      const metalMap = {
        'XAU': 'TVC:GOLD',
        'XAG': 'TVC:SILVER',
        'XPT': 'TVC:PLATINUM',
        'XPD': 'TVC:PALLADIUM'
      };
      return metalMap[symbol] || symbol;
    }
    return `NASDAQ:${symbol}`;
  };

  const AssetRow = ({ asset }) => (
    <div 
      className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group"
      onClick={() => setSelectedAsset(asset)}
      data-testid={`asset-${asset.symbol}`}
    >
      <div className="flex items-center gap-4">
        <div className={`min-w-12 h-12 px-2 rounded-xl flex items-center justify-center font-bold text-sm whitespace-nowrap shrink-0 ${
          asset.asset_type === 'crypto' ? 'bg-[#F7931A]/20 text-[#F7931A]' :
          asset.asset_type === 'metal' ? 'bg-yellow-500/20 text-yellow-500' :
          'bg-[#2962FF]/20 text-[#2962FF]'
        }`}>
          {asset.symbol}
        </div>
        <div>
          <p className="font-semibold text-white">{asset.symbol}</p>
          <p className="text-sm text-gray-500">{asset.name}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-lg font-bold text-white number">{formatCurrency(asset.price)}</p>
        <p className={`text-sm ${asset.change_percent >= 0 ? 'text-profit' : 'text-loss'}`}>
          {formatPercent(asset.change_percent)}
        </p>
      </div>
      <div className="hidden md:block text-right">
        <p className="text-sm text-gray-400">Volume</p>
        <p className="text-sm text-white number">{formatCurrency(asset.volume)}</p>
      </div>
      {asset.market_cap && (
        <div className="hidden lg:block text-right">
          <p className="text-sm text-gray-400">Market Cap</p>
          <p className="text-sm text-white number">{formatCurrency(asset.market_cap)}</p>
        </div>
      )}
      <div className="flex gap-2 ml-4">
        <Button 
          variant="ghost"
          size="sm"
          className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-white"
          onClick={(e) => {
            e.stopPropagation();
            openChart(getTradingViewSymbol(asset.symbol, asset.asset_type));
          }}
        >
          <LineChart className="w-4 h-4" />
        </Button>
        <Button 
          className="opacity-0 group-hover:opacity-100 transition-opacity btn-primary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = '/live-markets';
          }}
        >
          Trade Now
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="markets-page">
        {/* Live Banner */}
        <div className="p-4 rounded-xl bg-gradient-to-r from-[#00E676]/20 to-[#00E676]/10 border border-[#00E676]/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#00E676]/20 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-6 h-6 text-[#00E676]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white">Markets are LIVE</h3>
                <span className="px-2 py-0.5 text-xs rounded-full bg-[#00E676]/20 text-[#00E676] flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse"></span>
                  ACTIVE
                </span>
              </div>
              <p className="text-sm text-gray-400 mt-1">
                Real-time prices from CoinGecko and Alpha Vantage. Trade stocks, crypto, metals, and small businesses!
              </p>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 
              className="text-2xl md:text-3xl font-bold text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Markets
            </h1>
            <p className="text-gray-400 mt-1">Trade stocks, crypto, metals, and small businesses</p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-dark pl-10 w-64"
                data-testid="market-search"
              />
            </div>
            <Button 
              variant="outline" 
              className="btn-secondary"
              onClick={fetchMarkets}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Balance Card */}
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Available Balance</p>
                <p className="text-2xl font-bold text-[#00E676] number" data-testid="available-balance">
                  {formatCurrency(user?.balance)}
                </p>
              </div>
              <Button className="btn-primary" onClick={() => window.location.href = '/deposit'}>
                Add Funds
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* TradingView Chart Section */}
        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <LineChart className="w-5 h-5 text-[#2962FF]" />
                TradingView Chart
              </CardTitle>
              <div className="flex items-center gap-1">
                {timeframes.map((tf) => (
                  <button
                    key={tf.value}
                    onClick={() => setChartInterval(tf.value)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      chartInterval === tf.value
                        ? 'bg-[#2962FF] text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                    data-testid={`timeframe-${tf.label}`}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <TradingViewWidget symbol={chartSymbol} interval={chartInterval} />
          </CardContent>
        </Card>

        {/* Market Tabs */}
        <Tabs defaultValue="stocks" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 p-1">
            <TabsTrigger 
              value="stocks" 
              className="data-[state=active]:bg-[#2962FF] data-[state=active]:text-white"
              data-testid="tab-stocks"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Stocks ({stocks.length})
            </TabsTrigger>
            <TabsTrigger 
              value="crypto" 
              className="data-[state=active]:bg-[#F7931A] data-[state=active]:text-white"
              data-testid="tab-crypto"
            >
              <Coins className="w-4 h-4 mr-2" />
              Crypto ({crypto.length})
            </TabsTrigger>
            <TabsTrigger 
              value="metals" 
              className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black"
              data-testid="tab-metals"
            >
              <Diamond className="w-4 h-4 mr-2" />
              Metals ({metals.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stocks" className="mt-6">
            <div className="space-y-3">
              {filterAssets(stocks).map((asset, i) => (
                <AssetRow key={i} asset={asset} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="crypto" className="mt-6">
            <div className="space-y-3">
              {filterAssets(crypto).map((asset, i) => (
                <AssetRow key={i} asset={asset} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="metals" className="mt-6">
            <div className="space-y-3">
              {filterAssets(metals).map((asset, i) => (
                <AssetRow key={i} asset={asset} />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Trade Dialog */}
        <Dialog open={!!selectedAsset} onOpenChange={() => setSelectedAsset(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                  selectedAsset?.asset_type === 'crypto' ? 'bg-[#F7931A]/20 text-[#F7931A]' :
                  selectedAsset?.asset_type === 'metal' ? 'bg-yellow-500/20 text-yellow-500' :
                  'bg-[#2962FF]/20 text-[#2962FF]'
                }`}>
                  {selectedAsset?.symbol}
                </div>
                Trade {selectedAsset?.symbol}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Mini Chart */}
              <div className="rounded-xl overflow-hidden border border-white/10">
                <TradingViewMiniChart symbol={getTradingViewSymbol(selectedAsset?.symbol, selectedAsset?.asset_type)} />
              </div>

              {/* Price Info */}
              <div className="flex justify-between items-center p-4 rounded-xl bg-white/5">
                <div>
                  <p className="text-gray-400 text-sm">Current Price</p>
                  <p className="text-2xl font-bold text-white number">
                    {formatCurrency(selectedAsset?.price)}
                  </p>
                </div>
                <div className={`text-right ${selectedAsset?.change_percent >= 0 ? 'text-profit' : 'text-loss'}`}>
                  <div className="flex items-center gap-1">
                    {selectedAsset?.change_percent >= 0 ? 
                      <TrendingUp className="w-5 h-5" /> : 
                      <TrendingDown className="w-5 h-5" />
                    }
                    {formatPercent(selectedAsset?.change_percent)}
                  </div>
                  <p className="text-sm">24h change</p>
                </div>
              </div>

              {/* Buy/Sell Tabs */}
              <div className="flex gap-2">
                <Button
                  className={`flex-1 ${tradeType === 'buy' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setTradeType('buy')}
                  data-testid="trade-buy-btn"
                >
                  Buy
                </Button>
                <Button
                  className={`flex-1 ${tradeType === 'sell' ? 'bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80' : 'btn-secondary'}`}
                  onClick={() => setTradeType('sell')}
                  data-testid="trade-sell-btn"
                >
                  Sell
                </Button>
              </div>

              {/* Quantity Input */}
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Quantity</label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="input-dark text-lg"
                  min="0"
                  step="0.01"
                  data-testid="quantity-input"
                />
              </div>

              {/* Total */}
              <div className="flex justify-between items-center p-4 rounded-xl bg-white/5">
                <span className="text-gray-400">Total</span>
                <span className="text-xl font-bold text-white number">
                  {formatCurrency((parseFloat(quantity) || 0) * (selectedAsset?.price || 0))}
                </span>
              </div>

              {/* Balance */}
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Available Balance</span>
                <span className="text-[#00E676] number">{formatCurrency(user?.balance)}</span>
              </div>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setSelectedAsset(null)}
                className="btn-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleTrade}
                disabled={tradeLoading || !quantity}
                className={tradeType === 'buy' ? 'btn-primary' : 'bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80'}
                data-testid="confirm-trade-btn"
              >
                {tradeLoading ? (
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  `${tradeType === 'buy' ? 'Buy' : 'Sell'} ${selectedAsset?.symbol}`
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Full Chart Dialog */}
        <Dialog open={showChart} onOpenChange={setShowChart}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-5xl">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {chartSymbol} Advanced Chart
                </DialogTitle>
                <div className="flex items-center gap-1">
                  {timeframes.map((tf) => (
                    <button
                      key={tf.value}
                      onClick={() => setChartInterval(tf.value)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        chartInterval === tf.value
                          ? 'bg-[#2962FF] text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >
                      {tf.label}
                    </button>
                  ))}
                </div>
              </div>
            </DialogHeader>
            <div className="py-4">
              <TradingViewWidget symbol={chartSymbol} interval={chartInterval} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
